// @ts-nocheck
import {
	Button,
	CopyButton,
	Flex,
	Loader,
	Modal,
	Stack,
	Text,
	TextInput,
	Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconHelpHexagonFilled, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import httpClient from "@/config/httpClient";
import { handleApiResponse } from "@/utils/common/api/handleApiResponse";
import { endpoints } from "@/utils/common/requests/endpoints";
import { crewMembersQueries } from "@/utils/useQuery/queries/queries";
import classes from "./CrewMembers.module.css";
import CrewMember from "./partials/CrewMember/CrewMember";
import { useDeleteCrewMember } from "./requests/useDeleteCrewMember";
import { useUpdateCrewMemberRole } from "./requests/useUpdateCrewMemberRole";

export default function CrewMembers({
	opened = true,
	onClose = () => {},
	crew = {},
	onRenewInvitationId = () => {},
}) {
	const {
		data: crewMembersData,
		error,
		isLoading,
		invalidateKey,
	} = crewMembersQueries.getAll.fetchHook({
		args: {
			crewId: crew?.id,
		},
		enabled: Boolean(crew?.id),
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isMutatingUser, setIsMutatingUser] = useState(null);

	const { trigger: updateCrewMemberRole } = useUpdateCrewMemberRole({
		crewId: crew?.id,
	});
	const { trigger: deleteCrewMember } = useDeleteCrewMember({
		crewId: crew?.id,
		userId: isMutatingUser,
	});

	const invitationLink = `${import.meta.env.VITE_APP_URL}/crew-invitation?id=${crew?.invitationId}`;

	const handleRenew = async () => {
		setIsSubmitting(true);
		const { response } = await httpClient.patch(endpoints.crew.RENEW_INVITATION(crew?.id), {});

		if (response.invitationId) {
			onRenewInvitationId(response.invitationId);
			onClose();
		}

		setIsSubmitting(false);
	};

	const handleConfirmDeleteMember = ({ user }) => {
		modals.openConfirmModal({
			title: "Expulsar miembro",
			centered: true,
			children: (
				<Text size="sm">
					Esta acción expulsará a&nbsp;
					<Text component="span" c="blue">
						{user.username}
					</Text>
					&nbsp;de tu grupo&nbsp;
					<Text component="span" c="blue">
						{crew.name}
					</Text>
					.<br />
					Podrás volver a enviarle un enlace de invitación para que vuelva a unirse.
				</Text>
			),
			labels: {
				confirm: "Expulsar miembro",
				cancel: "Cancelar",
			},
			confirmProps: {
				color: "red",
				leftSection: <IconTrash />,
			},
			onConfirm: () =>
				handleDeleteCrewMember({
					userId: user.id,
					role: user.role,
				}),
		});
	};

	async function handleChangeRole({ userId, user, role }) {
		try {
			if (role === "#delete") {
				handleConfirmDeleteMember({
					user,
				});

				return;
			}

			setIsMutatingUser(userId);

			const apiResponse = await updateCrewMemberRole({
				payload: {
					userId,
					role,
				},
			});

			handleApiResponse({
				id: "update_crew_member_role",
				apiResponse: apiResponse,
				successTitle: "Rol cambiado con éxito",
				onSuccess: () => {
					invalidateKey(crewMembersQueries.getAll.key);
				},
			});
		} finally {
			setIsMutatingUser(null);
		}
	}

	async function handleDeleteCrewMember({ userId }) {
		try {
			setIsMutatingUser(userId);

			const apiResponse = await deleteCrewMember({
				userId,
			});

			handleApiResponse({
				id: "delete_crew_member",
				apiResponse: apiResponse,
				successTitle: "Miembro expulsado con éxito",
				onSuccess: () => {
					invalidateKey(crewMembersQueries.getAll.key);
				},
			});
		} finally {
			setIsMutatingUser(null);
		}
	}

	if (isLoading) return <Loader />;
	if (error) return <h1>Error</h1>;

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={
				<Text span size="xl" fw="bold">
					Gestionar miembros del grupo
				</Text>
			}
		>
			<Stack gap="xl">
				<Stack>
					<Text size="sm" fw={500}>
						Enlace de invitación
					</Text>

					<Flex gap="md" align="center">
						<TextInput
							value={invitationLink}
							className={classes.flexOne}
							readOnly
							onFocus={(ev) => {
								// Select all text
								ev.target.select();
							}}
						/>
						<CopyButton value={invitationLink}>
							{({ copied, copy }) => (
								<Button
									color={copied ? "teal" : "blue"}
									onClick={copy}
									loading={isSubmitting}
								>
									{copied ? "¡Copiado!" : "Copiar"}
								</Button>
							)}
						</CopyButton>
					</Flex>

					<Flex gap="md" align="center">
						<Button
							variant="light"
							style={{ flex: 1 }}
							onClick={handleRenew}
							loading={isSubmitting}
						>
							Renovar enlace de invitación
						</Button>

						<Tooltip
							events={{ hover: true, focus: true, touch: true }}
							label="Esta acción invalidará el enlace de invitación actual y creará uno nuevo. Todas las invitaciones enviadas y no aceptadas, dejarán de funcionar."
							multiline
							withArrow
							w={300}
						>
							<IconHelpHexagonFilled />
						</Tooltip>
					</Flex>
				</Stack>

				<Stack>
					<Text size="sm" fw={500}>
						Miembros
					</Text>

					<Stack>
						{(crewMembersData?.members ?? []).map((_member) => {
							return (
								<CrewMember
									key={_member.id}
									crewMember={_member}
									onChangeRole={({ userId, user, role }) => {
										handleChangeRole({ userId, user, role });
									}}
									isLoading={isMutatingUser === _member.id}
								/>
							);
						})}
					</Stack>
				</Stack>
			</Stack>
		</Modal>
	);
}
